import { IndexedDB } from 'services/indexed-db'
import { ITableConfigIndexedDB } from 'services/indexed-db/types'
import { FileRegistry } from 'utils/file-registry/FileRegistry'

export interface IThumbReadyData {
  assetId: string
  timestampSec: number
  bitmap: ImageBitmap
}

interface IThumbRecord {
  timestampSec: number
  blob: Blob
}

type TThumbSubscriber = (frames: Map<number, string>) => void

const DB_NAME = 'videoThumbsCache'
const EXPIRATION_TIME = 1000 * 60 * 60 * 24 * 7 // 7 days

const getAssetTableConfig = (assetId: string): ITableConfigIndexedDB => ({
  name: assetId,
  options: { keyPath: 'timestampSec' },
  expirationTime: EXPIRATION_TIME
})

class VideoThumbsService {
  private cache = new Map<string, Map<number, string>>()
  private subscribers = new Map<string, Set<TThumbSubscriber>>()
  private initialized = false
  private indexedDB = IndexedDB.getInstance(DB_NAME)

  private ensureAssetTable(assetId: string): void {
    this.indexedDB.registerTable(getAssetTableConfig(assetId))
  }

  async initialize(): Promise<void> {
    if (this.initialized) return
    this.initialized = true

    const tableNames = await this.indexedDB.getTableNames()
    for (const assetId of tableNames) {
      this.ensureAssetTable(assetId)
      const records = await this.indexedDB.readAllRecords<IThumbRecord>(assetId)
      if (!this.cache.has(assetId)) {
        this.cache.set(assetId, new Map())
      }
      for (const record of records) {
        const fileRegistry = FileRegistry.getInstance()
        const url = fileRegistry.registerFile(record.blob)
        this.cache.get(assetId)!.set(record.timestampSec, url)
      }
    }

    // Notify any subscribers that mounted before initialization completed
    this.cache.forEach((assetCache, assetId) => {
      const subs = this.subscribers.get(assetId)
      if (subs?.size) {
        const snapshot = new Map(assetCache)
        subs.forEach(cb => cb(snapshot))
      }
    })
  }

  async onThumbReady(data: IThumbReadyData): Promise<void> {
    const { assetId, timestampSec, bitmap } = data

    if (!Number.isFinite(timestampSec)) {
      bitmap.close()
      return
    }

    const assetCache = this.cache.get(assetId)
    if (assetCache?.has(timestampSec)) {
      bitmap.close()
      return
    }

    const width = bitmap.width
    const height = bitmap.height

    const canvas = new OffscreenCanvas(width, height)
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return
    }
    ctx.drawImage(bitmap, 0, 0)
    bitmap.close()

    const blob = await canvas.convertToBlob({
      type: 'image/jpeg',
      quality: 0.85
    })
    const fileRegistry = FileRegistry.getInstance()
    const url = fileRegistry.registerFile(blob)

    if (!this.cache.has(assetId)) {
      this.cache.set(assetId, new Map())
    }
    const updatedCache = this.cache.get(assetId)!
    updatedCache.set(timestampSec, url)

    // Notify immediately so UI shows thumbs without waiting for IDB
    const subs = this.subscribers.get(assetId)
    if (subs) {
      subs.forEach(cb => cb(new Map(updatedCache)))
    }

    // Persist in background — non-blocking
    this.ensureAssetTable(assetId)
    void this.indexedDB
      .saveRecord<IThumbRecord>(assetId, { timestampSec, blob })
      .catch(() => {
        // Thumb is in memory; IDB save failed — will be re-extracted on next load
      })
  }

  subscribe(assetId: string, cb: TThumbSubscriber): () => void {
    if (!this.subscribers.has(assetId)) {
      this.subscribers.set(assetId, new Set())
    }
    this.subscribers.get(assetId)!.add(cb)

    // Immediately notify with current cache if available
    const current = this.cache.get(assetId)
    if (current?.size) {
      cb(new Map(current))
    }

    return () => {
      this.subscribers.get(assetId)?.delete(cb)
    }
  }

  getCachedUrls(assetId: string): Map<number, string> {
    return new Map(this.cache.get(assetId) ?? [])
  }

  getCachedTimestamps(assetIds: string[]): Map<string, Set<number>> {
    const map = new Map<string, Set<number>>()
    for (const assetId of assetIds) {
      const assetCache = this.cache.get(assetId)
      map.set(assetId, assetCache ? new Set(assetCache.keys()) : new Set())
    }
    return map
  }

  clearAsset(assetId: string): void {
    const assetCache = this.cache.get(assetId)
    const fileRegistry = FileRegistry.getInstance()
    if (assetCache) {
      assetCache.forEach(url => fileRegistry.deleteFile(url))
      this.cache.delete(assetId)
    }
    this.ensureAssetTable(assetId)
    this.indexedDB.clearTable(assetId)
  }
}

export const videoThumbsService = new VideoThumbsService()
